import { serializeAsJSON, getSceneVersion, restore, restoreElements } from "@excalidraw/excalidraw";
import type { AppState, BinaryFiles, ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import type { ExcalidrawElement } from "@excalidraw/excalidraw/element/types";
import type { ExcalidrawSceneData } from "@excalidraw-ms/shared";

// Re-export validation from shared (single source of truth)
export { isValidExcalidrawScene as isValidScene } from "@excalidraw-ms/shared";

// ---------------------------------------------------------------------------
// Serialization (for saving)
// ---------------------------------------------------------------------------

/**
 * Serialize the current scene to the Excalidraw JSON data contract.
 * Wraps the library's `serializeAsJSON`.
 */
export function serializeScene(
  elements: readonly ExcalidrawElement[],
  appState: Partial<AppState>,
  files: BinaryFiles,
): string {
  const scene: ExcalidrawSceneData = JSON.parse(
    serializeAsJSON(elements, appState, files, "database"),
  );
  // Database mode omits files for OSS's separate image store; our documents are self-contained.
  const referencedFiles: BinaryFiles = Object.fromEntries(
    elements.flatMap((element) =>
      !element.isDeleted &&
      element.type === "image" &&
      element.fileId &&
      Object.hasOwn(files, element.fileId)
        ? [[element.fileId, files[element.fileId]]]
        : [],
    ),
  );
  return JSON.stringify({ ...scene, files: referencedFiles }, null, 2);
}

/**
 * Convenience: serialize directly from an ExcalidrawImperativeAPI ref.
 */
export function serializeFromAPI(api: ExcalidrawImperativeAPI): string {
  return serializeScene(api.getSceneElements(), api.getAppState(), api.getFiles());
}

// ---------------------------------------------------------------------------
// Restoration (for loading)
// ---------------------------------------------------------------------------

/**
 * Convert raw backend scene data into the format expected by `initialData`.
 * Uses the library's `restore` to normalise elements & appState.
 */
export function restoreScene(data: ExcalidrawSceneData) {
  const restored = restore(
    {
      elements: data.elements as ExcalidrawElement[],
      appState: data.appState as Partial<AppState>,
    },
    null,
    null,
    {
      refreshDimensions: true,
      repairBindings: true,
    },
  );
  return {
    elements: restored.elements,
    appState: restored.appState,
    files: (data.files ?? {}) as BinaryFiles,
  };
}

const ORDER_DIGITS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";

function nextWholeOrderKey(previous: string | null): string {
  if (previous === null) return "a0";

  let head = previous[0];
  const digits = previous.slice(1).split("");
  for (let position = digits.length - 1; position >= 0; position--) {
    const next = ORDER_DIGITS.indexOf(digits[position]) + 1;
    if (next < ORDER_DIGITS.length) {
      digits[position] = ORDER_DIGITS[next];
      return head + digits.join("");
    }
    digits[position] = ORDER_DIGITS[0];
  }

  if (head === "z") {
    throw new Error("Excalidraw element ordering space is exhausted");
  }
  head = String.fromCharCode(head.charCodeAt(0) + 1);
  digits.push(ORDER_DIGITS[0]);
  return head + digits.join("");
}

/**
 * Build an in-memory copy with canonical ordering keys while preserving array z-order.
 * The caller decides whether to persist it; this helper never mutates the source scene.
 */
export function repairSceneOrder(data: ExcalidrawSceneData): ExcalidrawSceneData {
  let previous: string | null = null;
  return {
    ...data,
    elements: data.elements.map((element) => {
      if (typeof element !== "object" || element === null || Array.isArray(element)) {
        throw new Error("Cannot repair ordering for a non-object scene element");
      }
      previous = nextWholeOrderKey(previous);
      return { ...element, index: previous };
    }),
  };
}

/**
 * Normalise a raw element array (e.g. received from remote) without
 * recalculating text dimensions (perf optimisation for tight sync loops).
 */
export function restoreRemoteElements(
  elements: readonly ExcalidrawElement[],
): readonly ExcalidrawElement[] {
  return restoreElements(elements, null, {
    refreshDimensions: false,
    repairBindings: true,
  }) as readonly ExcalidrawElement[];
}

// ---------------------------------------------------------------------------
// Version helpers
// ---------------------------------------------------------------------------

/**
 * Return the aggregate scene version (sum of all element versions).
 * Useful to detect whether the scene has changed.
 */
export function sceneVersion(elements: readonly ExcalidrawElement[]): number {
  return getSceneVersion(elements);
}

/**
 * Return the aggregate version of the live scene, for dirty tracking.
 *
 * This MUST use `getSceneElementsIncludingDeleted()`, not `getSceneElements()`.
 * Excalidraw invokes `onChange` with the including-deleted element set, so a
 * baseline captured from the non-deleted set can never match it once anything
 * has been deleted, leaving the document permanently marked "unsaved".
 */
export function currentSceneVersion(api: ExcalidrawImperativeAPI): number {
  return getSceneVersion(api.getSceneElementsIncludingDeleted());
}

// ---------------------------------------------------------------------------
// Validation — re-exported from @excalidraw-ms/shared above
// ---------------------------------------------------------------------------

/**
 * Create an empty scene conforming to the data contract.
 */
export function emptyScene(): ExcalidrawSceneData {
  return {
    type: "excalidraw",
    version: 2,
    elements: [],
    appState: {},
    files: {},
  };
}
